/**
 * Editorial blog posts.
 *
 * Open Graph prerender reads `path`, `title`, and `metaDescription`
 * (see frontend/scripts/prerender-open-graph.js). BlogIndexPage and
 * BlogPostPage render the rest.
 *
 * When you add a post:
 *   1) Keep `path` unique. Use /blog/<slug>.
 *   2) Add the URL to frontend/public/sitemap.xml.
 *   3) Keep metaDescription between 50 and 160 characters.
 */

export const blogPosts = [
  {
    slug: "ai-features-without-blowing-the-budget",
    path: "/blog/ai-features-without-blowing-the-budget",
    title: "AI features without blowing the budget",
    metaDescription:
      "Add one AI feature that pays for itself. Cap usage, cache repeats, pick a smaller model on purpose, and measure cost before you scale.",
    ogDescription:
      "One AI feature, a hard cap, a cache, and a model size you chose. Measure the bill before you add the next one.",
    ogImage: "/og/covers/ai-features-without-blowing-the-budget.png",
    imageAlt: "Maxiom Apps cover: AI features without blowing the budget",
    ogType: "article",
    published: "2026-10-13",
    publishedLabel: "October 13, 2026",
    eyebrow: "AI cost",
    dek: "A mobile app can add one AI feature without a runaway bill. Name the feature that earns its keep, then cap it, cache it, and measure it.",
    sections: [
      {
        id: "one-feature",
        heading: "Pick the one feature that earns its keep",
        paragraphs: [
          "Write the list of AI ideas. Then circle the one a person will use every week, and that saves a real minute or a real dollar.",
          "A chat box on the home screen is not that feature unless people already ask the same question in a text. A photo that fills a form, a reply the user can edit, or a search that finds the right record usually is.",
        ],
      },
      {
        id: "cap",
        heading: "Put a cap on before the first user",
        paragraphs: [
          "Set the ceiling in the same meeting as the feature. How many requests per person per day. How much the app may spend in a month. What the screen does when the cap is hit.",
          "The fallback is the old path. The form still works. The search still works. Nobody is stuck because a model went over budget.",
        ],
      },
      {
        id: "cache",
        heading: "Cache the answers you will see twice",
        paragraphs: [
          "Most of these features repeat. The same product question. The same status line. The same photo of a label.",
          "Store the answer for that input for a set time. When the input matches, serve the stored answer. You pay the model once, not every time the screen opens.",
        ],
      },
      {
        id: "model",
        heading: "Choose the model size on purpose",
        paragraphs: [
          "Start with the smallest model that does the job well enough. Filling a short form does not need the largest model in the catalog.",
          "Write the quality bar in one sentence, the way you would for a person doing the task. Try the small model on twenty real examples. If it clears the bar, ship it. Move up a size only when those examples fail.",
        ],
      },
    ],
    scopeIntro: "Take this into the build, before anyone turns usage loose.",
    keepTitle: "Do this in the first version",
    keep: [
      "One feature, with the person and the task written down.",
      "A daily cap per person and a monthly spend cap for the app.",
      "A screen that still finishes the job when the model is off.",
      "A cache for repeated inputs, with a clear time limit.",
      "The smallest model that passes a short set of real examples.",
      "A weekly read: requests, cache hits, and dollars spent.",
    ],
    cutTitle: "Leave this out until the number is real",
    cut: [
      "A second AI feature in the same release.",
      "An open chat with no cap.",
      "The largest model, because it looked good in a demo.",
      "A custom-trained model before you know which inputs repeat.",
      "New servers for a feature a hosted API can serve.",
      "A job that calls the model for every row, every night.",
    ],
    closing: [
      {
        id: "measure",
        heading: "Measure for two weeks before you scale",
        paragraphs: [
          "Ship to a small group. Read three numbers: how often people finish the task, how often the cache answers, and what you spent.",
          "If people finish and the spend stays inside the cap, raise the cap or add the next feature. If they do not finish, turn the feature off. The cap already told you the most it can cost.",
        ],
      },
      {
        id: "same-week",
        heading: "If the job is not the phone",
        paragraphs: [
          "This note is the cost of one AI feature inside a mobile app. The same week, three other Maxiom sites write the same idea for a different job.",
        ],
        bullets: [
          "[Hire AI engineers or train your team](https://maxiom.dev/hire-ai-engineers-vs-train-your-team), if you are deciding who should build it.",
          "[AI agents in real work: owners and guardrails](https://www.maxiomtech.com/ai-agents-in-real-work-owners-guardrails/), if someone has to own the agent after it ships.",
          "[AI agents for prior auth and intake](https://www.maxiomlabs.com/blog/ai-agents-prior-auth-intake), if the work is a clinic form.",
        ],
        after: [
          "When the feature is chosen and you need the app built around it, start with [AI app development](/ai-app-development-company).",
        ],
      },
    ],
    cta: {
      heading: "Bring the feature and the cap",
      body: "Schedule a call. A senior engineer will walk the feature, the cap, and the fallback with you. If you are still deciding whether to build, start with the free idea assessment.",
      primary: { label: "Schedule a Call", to: "/contact" },
      secondary: { label: "Start the free idea assessment", to: "/lp/free-app-idea-assessment" },
    },
  },
  {
    slug: "scoping-first-app-release",
    path: "/blog/scoping-first-app-release",
    title: "What to Cut From a First App Release",
    metaDescription:
      "Decide what a first mobile or web app must do on day one, and what waits. A practical cut list before you sign a vendor.",
    ogDescription:
      "One job a real user can finish. What stays in a first mobile or web release, and what waits until the path works.",
    ogType: "article",
    published: "2026-10-05",
    publishedLabel: "October 5, 2026",
    eyebrow: "First release",
    dek: "The brief for a first mobile or web app is usually a full product. The first release only needs one job a real user can finish.",
    sections: [
      {
        id: "job",
        heading: "Name the job before you list features",
        paragraphs: [
          "Write one sentence before anyone estimates. Name the person, the task, and what done looks like on a Tuesday afternoon.",
          "If that sentence needs the word platform, stop. You are describing a product line. The first release is one task.",
        ],
      },
      {
        id: "sentences",
        heading: "Use a sentence this specific",
        paragraphs: [
          "Swap in your user and your task.",
        ],
        bullets: [
          "A field tech closes a work order on the phone, and the office sees it the same day.",
          "A clinic coordinator books the next visit without a round of phone tag.",
          "A buyer reorders last month's parts from the truck.",
        ],
      },
      {
        id: "purpose",
        heading: "What the first release is for",
        paragraphs: [
          "The release answers one question: will this person complete this task more than once?",
          "A second role, a second app, and a settings screen do not answer that question. You will pay for them, and you will wait longer to learn whether anyone finishes the task.",
          "If the first screen your users open is a phone, start with [mobile app development](/services/mobile-app-development). If they live in a browser at a desk, start with [web app development](/services/web-app-development). Build the other client after the path works.",
        ],
      },
    ],
    scopeIntro: "Take this split into the scoping call.",
    keepTitle: "Keep this in the first release",
    keep: [
      "The happy path, start to finish, on the phone or browser people already use.",
      "One way to sign in. Email code, a company login, or a simple account. Use the one they already have.",
      "The records that path reads and writes, including the screen people see when there is no data yet.",
      "The two failures you can already name: no connection, and bad input.",
      "A plain way to see that someone finished. A short list or a spreadsheet export is enough.",
      "A store listing or a URL a real person can open.",
    ],
    cutTitle: "Cut this until the path works",
    cut: [
      "A second user type with its own screens.",
      "Several login methods side by side.",
      "In-app chat.",
      "A custom set of charts.",
      "Push campaigns, referrals, and badges.",
      "A redesign of the marketing site in the same contract.",
      "Offline mode, extra languages, tablet layouts, and a dark theme, unless the job fails without them.",
      "An admin tool that is really a second product.",
      "Extra integrations. Connect the one system that already holds the record.",
    ],
    closing: [
      {
        id: "contract",
        heading: "Put the cut list in the contract",
        paragraphs: [
          "Bring the job sentence and the cut list to the scoping call. Ask for two prices in writing: the path alone, and the path plus the items you are still unsure about.",
          "We write that list down in [discovery](/process), before a sprint is priced.",
        ],
        bullets: [
          "An in list and an out list.",
          "A working demo of the path every two weeks.",
          "Launch work on its own line: store submission or a production URL, plus basic monitoring.",
        ],
        after: [
          "If a line does not map to the job sentence, it waits. Give it a name and a later release so it is written down and not built now.",
        ],
      },
      {
        id: "read-aloud",
        heading: "Read the scope out loud",
        paragraphs: [
          "For each feature, ask one question: if this is missing on launch day, can the user still finish the job?",
          "If the answer is yes, it waits. If the answer is no, it stays.",
          "Do this before you sign. Finding the extra screens in sprint three costs more than finding them on a printout tonight.",
        ],
      },
    ],
    cta: {
      heading: "Bring the job sentence and the cut list",
      body: "Schedule a call. A senior engineer will walk the actual constraint with you. If you are still deciding whether to build, start with the free idea assessment.",
      primary: { label: "Schedule a Call", to: "/contact" },
      secondary: { label: "Start the free idea assessment", to: "/lp/free-app-idea-assessment" },
    },
  },
  {
    slug: "mobile-app-launch-security-checklist",
    path: "/blog/mobile-app-launch-security-checklist",
    title: "Mobile app launch security checklist",
    metaDescription:
      "Walk this list before a mobile app goes live. Secrets, sign-in, every API call, data on the phone, and who gets the call.",
    ogDescription:
      "Nine checks before the app goes live. No secrets in the build, a real sign-out, and a person named for the night it breaks.",
    ogImage: "/og/covers/mobile-app-launch-security-checklist.png",
    imageAlt: "Maxiom Apps cover: Mobile app launch security checklist",
    ogType: "article",
    published: "2026-10-13",
    publishedLabel: "October 13, 2026",
    eyebrow: "Launch security",
    dek: "A founder can walk this list the week before a mobile app goes live. Secrets, sign-in, the API, the phone, and who gets the call.",
    sections: [
      {
        id: "secrets",
        heading: "Look inside the build, not the repo",
        paragraphs: [
          "The release build is what the store ships. Open that file and search for keys, passwords, and private URLs. A string you can read is a string anyone can read.",
          "If a key has to live somewhere, it lives on the server. The app asks for a short-lived token. It does not carry the key.",
        ],
      },
      {
        id: "session",
        heading: "Sign-in, and what happens when a phone is lost",
        paragraphs: [
          "Use one way to sign in, the one people already have. A session should expire. From the server, you should be able to sign that phone out.",
          "A screen that stays open forever is not a convenience. It is a phone on a train with the account still inside.",
        ],
      },
      {
        id: "api",
        heading: "The API checks every call",
        paragraphs: [
          "Hiding a button does not hide the record. Every call has to ask who this person is, and whether they may see this row.",
          "Try it the unkind way. Call the API as a user who should not have the record. If the record comes back, the app is not ready.",
        ],
      },
      {
        id: "device",
        heading: "What the phone is allowed to keep",
        paragraphs: [
          "Store only what the next screen needs. Put it in the platform secure store. A plain file, a copied database, or a note in preferences is the wrong place for a token or a customer list.",
          "The connection is HTTPS, and the app refuses a certificate it does not trust. A cafe network is not a reason to turn that off.",
        ],
      },
      {
        id: "sdks",
        heading: "Name every SDK, then match the store form",
        paragraphs: [
          "Each third-party kit sends something. Write down the name and what it collects. If you cannot say, take it out.",
          "The App Store and Play privacy labels are the same list. If the label says you do not collect location and a kit does, fix the label or remove the kit before you submit.",
        ],
      },
      {
        id: "logs",
        heading: "Logs without the person, and a name for the night it breaks",
        paragraphs: [
          "Crash reports and support logs can say which screen failed. They should not say the person's name, email, token, or the text they typed.",
          "Write down who gets the call when the app breaks on a Saturday. A name and a number that rings. If that line is blank, you do not have a launch plan.",
        ],
      },
    ],
    scopeIntro: "Print this. Walk it with the person who will press submit.",
    keepTitle: "Walk this before you ship",
    keep: [
      "Unzip the release build and search for keys, passwords, and private URLs. A string in the app is a string in the store.",
      "Sign-in uses one method people already have. A session expires. A lost phone can be signed out from the server.",
      "Every API call checks who is asking and which record they may see. Hiding the button is not the check.",
      "The phone stores only what the next screen needs, in the platform secure store, not a plain file.",
      "The app talks only over HTTPS and refuses a bad certificate.",
      "Each third-party SDK is named, with what it sends off the phone, and you can ship without it.",
      "The App Store and Play privacy labels match what the app actually sends.",
      "Crash logs and support logs have no names, emails, tokens, or message text.",
      "One person is named for the night it breaks, with a number that rings.",
    ],
    cutTitle: "Do not ship with this",
    cut: [
      "A debug menu or a test login left in the release build.",
      "A shared password or API key baked into the app.",
      "An API that trusts the call because it came from your app.",
      "A full customer list saved on the phone so a screen can scroll offline.",
      "An SDK added for a feature you already cut.",
      "Privacy labels copied from a template.",
      "Logs that include the request body.",
      "On-call left as a problem for next week.",
    ],
    closing: [
      {
        id: "release-build",
        heading: "Do it on the build you will submit",
        paragraphs: [
          "Walk the list on the release build, not the one on a developer's laptop. The laptop build often still has the debug menu. The store build is the one a stranger installs.",
          "If a line fails, fix it and walk that line again. Then submit.",
        ],
      },
      {
        id: "same-week",
        heading: "If the job is not the phone",
        paragraphs: [
          "This note is the list before a mobile app goes live. The same week, three other Maxiom sites write the same idea for a different job.",
        ],
        bullets: [
          "[Vetting outside developers: security and access](https://maxiom.dev/vetting-outside-developers-security-access), if the people building it are not on your payroll.",
          "[SOC 2 without slowing down](https://www.maxiomtech.com/soc-2-without-slowing-down/), if the company needs the controls and the product still has to ship.",
          "[HIPAA cloud hosting: what you actually need](https://www.maxiomlabs.com/blog/hipaa-cloud-hosting), if the app holds patient data and the question is the hosting.",
        ],
        after: [
          "When you want someone to walk the build with you before it ships, start with [security audits](/services/security-audits).",
        ],
      },
    ],
    cta: {
      heading: "Bring the release build",
      body: "Schedule a call. A senior engineer will walk the list with you on the build you plan to submit. If you are still deciding whether to build, start with the free idea assessment.",
      primary: { label: "Schedule a Call", to: "/contact" },
      secondary: { label: "Start the free idea assessment", to: "/lp/free-app-idea-assessment" },
    },
  },
  {
    slug: "real-cost-of-a-cheap-mobile-app",
    path: "/blog/real-cost-of-a-cheap-mobile-app",
    title: "The real cost of a cheap mobile app",
    metaDescription:
      "A low bid that skips sign-in, device security, store rules, and a real backend usually costs more in rewrites and lost trust.",
    ogDescription:
      "A bid that skips sign-in, the phone, the stores, and a backend is a second build, and users who do not come back.",
    ogImage: "/og/covers/real-cost-of-a-cheap-mobile-app.png",
    imageAlt: "Maxiom Apps cover: The real cost of a cheap mobile app",
    ogType: "article",
    published: "2026-10-20",
    publishedLabel: "October 20, 2026",
    eyebrow: "Cheap bids",
    dek: "A low price that skips sign-in, the phone, the store rules, and a real backend is the cost of a second build. The first users are the ones who do not come back.",
    sections: [
      {
        id: "four-lines",
        heading: "Four lines a cheap bid leaves blank",
        paragraphs: [
          "The number is low because four things are not in it. Sign-in you can revoke. Data that stays safe on a lost phone. A store listing that matches what the app sends. A backend that checks every call.",
          "If the proposal does not name those four, you are buying a demo. A demo is not the app a stranger installs on a Tuesday.",
        ],
      },
      {
        id: "auth",
        heading: "Sign-in that you can turn off",
        paragraphs: [
          "A cheap build often shares one password, or it stores a token that never expires. When a phone is lost, you cannot sign that person out. You find out when a customer writes in.",
          "The line on the quote is simple. One way in, the one people already have. A session that ends. A control on the server that kills that phone.",
        ],
      },
      {
        id: "device",
        heading: "What the phone is allowed to keep",
        paragraphs: [
          "Keys in the app, a customer list in a plain file, and a connection that trusts any certificate. Anyone who installs the build can read a string you left in it.",
          "The line on the quote: secrets stay on the server. The phone keeps only what the next screen needs, in the platform secure store. The app talks only over HTTPS and refuses a certificate it does not trust.",
        ],
      },
      {
        id: "stores",
        heading: "The stores are a bill, not a favor",
        paragraphs: [
          "Apple and Google reject an app whose privacy label does not match the kits inside. A bid that says the team will submit it, and does not name the label, the account, or who answers the reviewer, is leaving the rejection on you.",
          "The line on the quote: a privacy label that matches the build, a developer account you control, and one person named to answer the review.",
        ],
      },
      {
        id: "backend",
        heading: "A screen is not a backend",
        paragraphs: [
          "A demo that writes to a spreadsheet, or a database with the rules off, looks finished in a meeting. The first person who guesses the address can read every row. Hiding the button does not hide the record.",
          "The line on the quote: an API that asks who is calling and which record they may see. The phone does not get to decide.",
        ],
      },
    ],
    scopeIntro: "Print the quote. If a line below is missing, ask for it as its own price before you sign.",
    keepTitle: "Check these lines on the quote",
    keep: [
      "One way to sign in, the one people already have. A session that ends. A lost phone can be signed out from the server.",
      "No keys, passwords, or private URLs inside the release build. Secrets stay on the server.",
      "The phone stores only what the next screen needs, in the platform secure store, not a plain file.",
      "The app talks only over HTTPS and refuses a certificate it does not trust.",
      "The App Store and Play privacy labels match what the app actually sends.",
      "A developer account you control, and a person named to answer the reviewer.",
      "Every API call checks who is asking and which record they may see.",
      "Those items are their own lines on the price, not a sentence that says the team will handle it.",
    ],
    cutTitle: "Do not sign a bid that says this",
    cut: [
      "A shared password, or a test login left in the release.",
      "A token that never expires, and no way to sign a lost phone out.",
      "A customer list saved on the phone so a screen can scroll.",
      "A database the phone can read with the rules turned off.",
      "Privacy labels copied from a template.",
      "The store account figured out at the end.",
      "A spreadsheet the app writes to directly.",
      "A second quote, later, that only covers the screens the first quote skipped.",
    ],
    closing: [
      {
        id: "second-bill",
        heading: "The second bill is the one that hurts",
        paragraphs: [
          "The rewrite is the first cost. You pay a team to put back the four lines the first bid skipped. The second cost is the people who installed the broken app. They do not install the next one.",
          "Refunds, a pulled listing, and a week of explaining to the customers you already had. Building the four lines once is slower than the low bid. It is cheaper than paying for the app twice and starting the trust over.",
        ],
      },
      {
        id: "same-week",
        heading: "If the job is not the phone",
        paragraphs: [
          "This note is the cost of a cheap mobile app. The same week, three other Maxiom sites write the same idea for a different job.",
        ],
        bullets: [
          "[The real cost of a bad developer hire](https://maxiom.dev/real-cost-of-a-bad-developer-hire), if the risk is the person you are about to sign.",
          "[The real cost of technical debt](https://www.maxiomtech.com/real-cost-of-technical-debt/), if the product already ships and each change takes longer.",
          "[The real cost of HIPAA shortcuts](https://www.maxiomlabs.com/blog/real-cost-of-hipaa-shortcuts), if the app holds patient data and someone wants to bolt the rules on at the end.",
        ],
        after: [
          "When you want the first build done with those four lines in the price, start with [MVP app development](/mvp-app-development-company).",
        ],
      },
    ],
    cta: {
      heading: "Bring the quote",
      body: "Schedule a call. A senior engineer will mark the four lines on the bid with you. If you are still deciding whether to build, start with the free idea assessment.",
      primary: { label: "Schedule a Call", to: "/contact" },
      secondary: { label: "Start the free idea assessment", to: "/lp/free-app-idea-assessment" },
    },
  },
  {
    slug: "who-owns-the-app-after-launch",
    path: "/blog/who-owns-the-app-after-launch",
    title: "Who owns the app after launch",
    metaDescription:
      "After launch, name who ships store updates, reads crash reports, handles OS upgrades, and renews certificates before they expire.",
    ogDescription:
      "Store updates, crash reports, OS upgrades, expiring keys, and a name for the night the app breaks.",
    ogImage: "/og/covers/who-owns-the-app-after-launch.png",
    imageAlt: "Maxiom Apps cover: Who owns the app after launch",
    ogType: "article",
    published: "2026-10-27",
    publishedLabel: "October 27, 2026",
    eyebrow: "After launch",
    dek: "The app is in the stores. Someone still has to ship the next build, read the crashes, and answer when a certificate expires on a Friday.",
    sections: [
      {
        id: "stores",
        heading: "The stores keep moving after you ship",
        paragraphs: [
          "Apple and Google change the rules after the first approval. A build that passed in March can fail a new privacy question in October. Someone has to open the developer account, match the label to what the app sends, and submit again.",
          "If that account is a vendor's personal email, you do not own the app. The account, the listing, and the person who answers the reviewer belong to the company.",
        ],
      },
      {
        id: "crashes",
        heading: "Someone reads the crash report the next morning",
        paragraphs: [
          "The number moves after launch. A crash on the sign-in screen is not a ticket for next quarter. Write down who looks, and what bad means: crash-free sessions drop, or a new stack shows up in the top five.",
          "The report can name the screen. It should not name the person, the email, or the token. The same person who reads it can say whether the next build goes out this week.",
        ],
      },
      {
        id: "os",
        heading: "The new OS is a date, not a surprise",
        paragraphs: [
          "iOS and Android ship a new version every year. The app you already have will be asked to run on it. A beta in the summer is cheaper than a broken screen in September.",
          "Name the person who installs the beta and walks the main job: sign in, the one task people came for, and the way back out. If that walk fails, the fix is a store update, not a hope that users stay on the old OS.",
        ],
      },
      {
        id: "certs",
        heading: "Certificates and keys expire on a Friday",
        paragraphs: [
          "Apple distribution certificates, push keys, Play app signing, and the API keys on the server all have a date. A certificate that dies on a Friday takes the next update with it. People still have the old build. You cannot ship the fix.",
          "Put the date on a calendar the company can see. The keys live in a place the company controls, not on one engineer's laptop. When that person is out, someone else can still sign the build.",
        ],
      },
      {
        id: "phone",
        heading: "Who picks up the phone",
        paragraphs: [
          "When the app breaks on a Saturday, one name rings. Not a shared inbox. Not a vendor who will see it Monday. A person, a number, and what they are allowed to ship without a meeting.",
          "Write that name next to the store account and the crash line. If the line is blank, you do not have an owner. You have a launch.",
        ],
      },
    ],
    scopeIntro: "Write the name before the first weekend the app is live.",
    keepTitle: "Name these before you call it done",
    keep: [
      "The Apple and Google accounts in the company's name, with a person who can submit.",
      "A crash report someone reads the next morning, with a number that means the next build goes out.",
      "A date for the next iOS and Android beta, and a person who walks the main job on it.",
      "Every certificate and key on a calendar, stored where the company can still sign a build if one person is out.",
      "One name and a number for the night it breaks, and what that person may ship without a meeting.",
      "Logs that name the screen, not the person, the email, or the token.",
    ],
    cutTitle: "Do not leave launch like this",
    cut: [
      "A store account on a vendor's personal email.",
      "Crash reports that nobody opens until a user writes in.",
      "The new OS treated as a surprise in September.",
      "A push certificate that expires on a Friday, with the only copy on a laptop.",
      "On-call written as the team, with no name.",
      "A shared inbox as the only way to report that the app is down.",
      "A fix that waits for a meeting before anyone can ship it.",
    ],
    closing: [
      {
        id: "blank-line",
        heading: "A blank line is the expensive part",
        paragraphs: [
          "The first cost is the weekend the certificate dies and the update cannot ship. The second cost is the people who already installed the app. They do not file a ticket. They stop opening it.",
          "Naming the owner is slower than a launch party. It is cheaper than finding out on a Saturday that nobody can sign the build.",
        ],
      },
      {
        id: "same-week",
        heading: "If the job is not the phone",
        paragraphs: [
          "This note is who owns a mobile app after it is in the stores. The same week, three other Maxiom sites write the same idea for a different job.",
        ],
        bullets: [
          "[Who owns the code after the contract ends](https://maxiom.dev/who-owns-the-code-after-the-contract-ends), if the question is the repo, the access, and a clean handoff.",
          "[Who owns production after launch](https://www.maxiomtech.com/who-owns-production-after-launch/), if the question is on-call, deploys, and a named owner for every system.",
          "[Who owns PHI systems after go-live](https://www.maxiomlabs.com/blog/who-owns-phi-systems-after-go-live), if the app holds patient data and someone has to answer when that data is wrong.",
        ],
        after: [
          "When you are still building the first version, start with [mobile app development](/services/mobile-app-development).",
        ],
      },
    ],
    cta: {
      heading: "Bring the name",
      body: "Schedule a call. A senior engineer will walk the store accounts, the crash line, and who picks up the phone. If you are still deciding whether to build, start with the free idea assessment.",
      primary: { label: "Schedule a Call", to: "/contact" },
      secondary: { label: "Start the free idea assessment", to: "/lp/free-app-idea-assessment" },
    },
  },
  {
    slug: "questions-to-ask-a-mobile-app-agency",
    path: "/blog/questions-to-ask-a-mobile-app-agency",
    title: "Questions to ask a mobile app agency before you sign",
    metaDescription:
      "Ask who builds the app, who owns the code and the store accounts, how they test on real phones, and what happens after launch.",
    ogDescription:
      "Who builds it, who owns the code and the stores, how they test on real phones, and who stays after launch.",
    ogImage: "/og/covers/questions-to-ask-a-mobile-app-agency.png",
    imageAlt: "Maxiom Apps cover: Questions to ask a mobile app agency before you sign",
    ogType: "article",
    published: "2026-11-03",
    publishedLabel: "November 3, 2026",
    eyebrow: "Before you sign",
    dek: "The proposal looks finished. Before you sign, ask who writes the code, who holds the accounts, how they test on real phones, and who stays after launch.",
    sections: [
      {
        id: "who-builds",
        heading: "Who actually builds it",
        paragraphs: [
          "Ask for the names. The people who will open the repo, not the person who sold the project.",
          "If the answer is a team, a bench, or we will staff it later, you do not know who builds it. Ask which person is on the app next month, and what happens when that person leaves.",
          "A senior engineer on the first call is a fair ask. You should hear from the person who will make the calls, not a slide about the company.",
        ],
      },
      {
        id: "code",
        heading: "Who owns the code",
        paragraphs: [
          "The repo belongs to the company. Your GitHub or GitLab org. Your admin seat. A copy you can open the day the contract ends.",
          "A zip file at the end is not ownership. If the only history lives in the vendor's account, you are renting the app.",
          "Ask what you get if you stop. The repo, the branches, the keys, and a note on how to build it. Put that in the contract before the first sprint.",
        ],
      },
      {
        id: "stores",
        heading: "Who owns the App Store and Play accounts",
        paragraphs: [
          "Apple and Google accounts are the app. If they sit on a vendor's personal email, the vendor owns the listing, the reviews, and the next update.",
          "The accounts go in the company's name. You are the account holder. The agency is a user you can remove.",
          "Ask who answers the reviewer, who pays the Apple and Google fees, and whose name is on the agreement. If the proposal says the team will handle the stores, ask whose login that is.",
        ],
      },
      {
        id: "devices",
        heading: "How they test on real devices",
        paragraphs: [
          "A simulator is not a phone. Ask which devices they keep, and which ones your users actually hold.",
          "The list is short and specific. A current iPhone. An older iPhone people still have. A current Pixel or Samsung. One Android that is two years old. The tablet, if anyone uses a tablet.",
          "Ask them to name a bug they found on a device that a simulator missed. If they cannot name one, they are testing on a computer.",
        ],
      },
      {
        id: "after",
        heading: "What happens after launch",
        paragraphs: [
          "Launch is a Tuesday. The next Tuesday the app still needs a person. A store update. A crash. A certificate that expires. A new iOS.",
          "Ask for the name. Not the team. The person who looks the next morning, and what they may ship without a new contract.",
          "If after launch is a separate conversation, you are buying a launch. Write the first 90 days into the same agreement, or know that you will be shopping again in week two.",
        ],
      },
    ],
    scopeIntro: "Take these questions into the call, before you sign.",
    keepTitle: "Ask these before you sign",
    keep: [
      "The names of the people who will write the code, and who replaces them if they leave.",
      "The repo in the company's account, with an admin seat you control.",
      "What you receive if you stop: the history, the keys, and a note on how to build it.",
      "App Store and Play accounts in the company's name. The agency is a user you can remove.",
      "Who answers the reviewer, and who pays the Apple and Google fees.",
      "The real phones they test on, including one older phone your users still have.",
      "A bug they found on a device that a simulator missed.",
      "One name for the weeks after launch, and what that person may ship without a new contract.",
    ],
    cutTitle: "Do not sign if you hear this",
    cut: [
      "We will staff the team after you sign.",
      "The code is yours at the end, as a zip file.",
      "The repo stays in our GitHub until the project is done.",
      "We will handle the App Store and Play accounts.",
      "The store login is on someone's personal email.",
      "We test on the latest simulator.",
      "After launch is a different contract. We can talk about it then.",
      "The team will monitor it. No name.",
    ],
    closing: [
      {
        id: "blank-answer",
        heading: "A blank answer is the expensive part",
        paragraphs: [
          "The first cost is the month you cannot ship, because the store login is someone else's. The second cost is the people who already installed the app. They do not wait while you sort out who owns it.",
          "Asking the questions is slower than signing the proposal. It is cheaper than paying a second team to get the accounts back.",
        ],
      },
      {
        id: "same-week",
        heading: "If the job is not the phone",
        paragraphs: [
          "This note is the questions to ask a mobile app agency. The same week, three other Maxiom sites write the same idea for a different job.",
        ],
        bullets: [
          "[How to compare developer staffing vendors](https://maxiom.dev/how-to-compare-developer-staffing-vendors), if you are hiring people rather than an agency to build the app.",
          "[How to choose a software partner](https://www.maxiomtech.com/how-to-choose-a-software-partner/), if the work is a software partner, not only a phone app.",
          "[Choosing a healthcare software vendor](https://www.maxiomlabs.com/blog/choosing-a-healthcare-software-vendor), if the app holds patient data.",
        ],
        after: [
          "When you want to see how a build actually runs, start with [our process](/process).",
        ],
      },
    ],
    cta: {
      heading: "Bring the proposal",
      body: "Schedule a call. A senior engineer will walk these questions against the proposal with you. If you are still deciding whether to build, start with the free idea assessment.",
      primary: { label: "Schedule a Call", to: "/contact" },
      secondary: { label: "Start the free idea assessment", to: "/lp/free-app-idea-assessment" },
    },
  },
];

export function getBlogPost(slug) {
  return blogPosts.find((post) => post.slug === slug) || null;
}
